import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Copy, Shield, Image, Loader2, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import PatternFirstCard from "@/components/PatternFirstCard";
import ViralPosterCard from "@/components/ViralPosterCard";
import EducationalExplainerCard from "@/components/EducationalExplainerCard";
import RehearsalCard from "@/components/RehearsalCard";
import { routeAnalysis, type AnalysisResult } from "@/lib/resultRouter";
import {
  generateGroundedScript,
  generateHookVariants,
  generateCaptionVariants,
  generateCTAVariants,
  PLATFORM_OPTIONS,
  type Platform,
} from "@/lib/scriptGenerator";
import { TACTICAL_DESCRIPTIONS } from "@/lib/tacticalDescriptions";
import html2canvas from "html2canvas";

interface AnalysisCase {
  id: string;
  raw_text: string;
  toxicity_score: number;
  manipulation_primary_type: string | null;
  grey_rock_response: string | null;
  created_at: string;
}

export interface RehearsalData {
  rehearsal_id: string;
  tactics: string[];
  readiness_score: number;
  exchange_count: number;
}

type CardType = "pattern" | "creator" | "explainer" | "rehearsal";

const CARD_OPTIONS: { key: CardType; label: string; desc: string }[] = [
  { key: "pattern", label: "Pattern Card", desc: "9:16 · Pattern-first" },
  { key: "creator", label: "Creator Card", desc: "9:16 · Social hook" },
  { key: "explainer", label: "Explainer Card", desc: "4:5 · Educational" },
  { key: "rehearsal", label: "Rehearsal Card", desc: "9:16 · Debrief" },
];

// ─── Variant Block ───
function VariantBlock({
  title,
  variants,
  isOpen,
  onToggle,
}: {
  title: string;
  variants: { style: string; label: string; text: string }[];
  isOpen: boolean;
  onToggle: () => void;
}) {
  const copyVariant = async (text: string) => {
    await navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <CollapsibleTrigger className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-border bg-secondary/30 hover:bg-secondary/50 transition-colors text-xs font-semibold text-foreground">
        {title}
        <ChevronDown
          className={cn(
            "w-3.5 h-3.5 text-muted-foreground transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-1.5 space-y-1.5">
        {variants.map((v) => (
          <div
            key={v.style}
            className="flex items-start gap-2 p-2.5 rounded-lg border border-border bg-card"
          >
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground mb-0.5">
                {v.label}
              </p>
              <p className="text-xs text-foreground leading-relaxed">{v.text}</p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => copyVariant(v.text)}
              className="shrink-0 text-muted-foreground hover:text-cyan h-8 w-8 p-0 rounded-md transition-colors"
            >
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

// ─── Script Factory Modal ───
export default function ScriptFactoryModal({
  open,
  onClose,
  item,
  rehearsalData,
  initialCardType,
}: {
  open: boolean;
  onClose: () => void;
  item: AnalysisCase | null;
  rehearsalData?: RehearsalData | null;
  initialCardType?: CardType;
}) {
  const patternCardRef = useRef<HTMLDivElement>(null);
  const creatorCardRef = useRef<HTMLDivElement>(null);
  const explainerCardRef = useRef<HTMLDivElement>(null);
  const rehearsalCardRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [cardType, setCardType] = useState<CardType>(initialCardType || "pattern");
  const [openVariant, setOpenVariant] = useState<string | null>(null);
  const [platform, setPlatform] = useState<Platform>("tiktok");
  const [rehearsalScripts, setRehearsalScripts] = useState<Record<string, any> | null>(null);
  const [rehearsalLoading, setRehearsalLoading] = useState(false);
  const [rehearsalError, setRehearsalError] = useState(false);

  useEffect(() => {
    if (!open || !rehearsalData || !item) {
      setRehearsalScripts(null);
      setRehearsalError(false);
      return;
    }
    setRehearsalLoading(true);
    setRehearsalError(false);
    supabase.functions.invoke("generate-rehearsal-scripts", {
      body: {
        source_message: item.raw_text,
        tactics: rehearsalData.tactics,
        readiness_score: rehearsalData.readiness_score,
        exchange_count: rehearsalData.exchange_count,
      },
    }).then(({ data, error }) => {
      if (error || !data) {
        console.error("Rehearsal script generation error:", error);
        setRehearsalError(true);
      } else {
        setRehearsalScripts(data);
      }
      setRehearsalLoading(false);
    });
  }, [open, rehearsalData, item]);

  if (!item) return null;

  const analysis = routeAnalysis(item.raw_text);
  const score = analysis.score;
  const verdict = analysis.verdict;
  const pattern = analysis.primary_pattern
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const band = score >= 7 ? "high" : score >= 4 ? "moderate" : "low";

  const scripts: {
    key: "expose" | "greyrock" | "educational";
    label: string;
    role: string;
    hook: string;
    color: string;
  }[] = [
    {
      key: "expose",
      label: "The Exposé",
      role: "Callout / Breakdown",
      hook:
        band === "high"
          ? `"What ${pattern.toLowerCase()} actually looks like…"`
          : `"This sounds casual, but here's what it's doing…"`,
      color: "text-red-400 border-red-500/30 bg-red-500/5",
    },
    {
      key: "greyrock",
      label: "The Response Script",
      role: "What to say back",
      hook: `"What to say back when you hear ${pattern.toLowerCase()}…"`,
      color: "text-cyan border-cyan/30 bg-cyan/5",
    },
    {
      key: "educational",
      label: "The Explainer",
      role: "Educational version",
      hook: `"How ${pattern.toLowerCase()} works in everyday language…"`,
      color: "text-amber-400 border-amber-500/30 bg-amber-500/5",
    },
  ];

  const hookVariants = generateHookVariants(item.raw_text, analysis, platform);
  const captionVariants = generateCaptionVariants(item.raw_text, analysis, platform);
  const ctaVariants = generateCTAVariants(item.raw_text, analysis, platform);

  const copyScript = async (type: "expose" | "greyrock" | "educational") => {
    const text = generateGroundedScript(type, item.raw_text, analysis, platform);
    await navigator.clipboard.writeText(text);
    toast.success("Script copied to clipboard");
  };

  const getActiveRef = () => {
    if (cardType === "creator") return creatorCardRef;
    if (cardType === "explainer") return explainerCardRef;
    if (cardType === "rehearsal") return rehearsalCardRef;
    return patternCardRef;
  };

  const hasRehearsalData = !!(rehearsalData && rehearsalData.tactics.length > 0);

  const downloadShareCard = async () => {
    const ref = getActiveRef();
    if (!ref.current) return;
    setIsDownloading(true);
    try {
      await document.fonts.ready;
      const canvas = await html2canvas(ref.current, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#121212",
      });
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `greyrock-${cardType}-${score.toFixed(1)}.png`;
      a.click();
      toast.success("Share card downloaded");

      // Upload to Supabase Storage
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user && item.id) {
          const res = await fetch(url);
          const pngBlob = await res.blob();
          const fileName = `${user.id}/${item.id}/${platform}_${cardType}_${Date.now()}.png`;

          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('card-assets')
            .upload(fileName, pngBlob, {
              contentType: 'image/png',
              upsert: true,
            });

          if (!uploadError && uploadData) {
            const { data: { publicUrl } } = supabase.storage
              .from('card-assets')
              .getPublicUrl(uploadData.path);

            await supabase
              .from('neutralizations')
              .update({ card_url: publicUrl })
              .eq('id', item.id);
          }
        }
      } catch (uploadErr) {
        console.error("Card upload failed (non-blocking):", uploadErr);
      }
    } catch {
      toast.error("Failed to generate share card");
    } finally {
      setIsDownloading(false);
    }
  };

  const interpretation =
    TACTICAL_DESCRIPTIONS[analysis.primary_pattern.toUpperCase()]?.shortExplanation ||
    analysis.nuance ||
    `Detected ${pattern} pattern`;

  const selectedCardOption = CARD_OPTIONS.find((c) => c.key === cardType)!;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto bg-card border-border">
          <DialogHeader className="pb-0">
            <DialogTitle className="text-foreground flex items-center gap-2 text-sm">
              <Shield className="w-4 h-4 text-cyan" />
              Script Factory
            </DialogTitle>
          </DialogHeader>

          {/* Original message preview */}
          <div className="px-3 py-2 rounded-lg bg-secondary/50 border border-border text-xs text-muted-foreground line-clamp-2">
            "{item.raw_text}"
          </div>

          {/* Platform Preset Selector */}
          <div className="flex items-center gap-1">
            {PLATFORM_OPTIONS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPlatform(p.key)}
                className={cn(
                  "flex-1 px-1.5 py-1 rounded-md border text-center transition-all text-[10px]",
                  platform === p.key
                    ? "border-cyan/40 bg-cyan/10 text-cyan font-semibold"
                    : "border-border bg-secondary/20 text-muted-foreground hover:bg-secondary/40"
                )}
              >
                <span className="block text-xs leading-none mb-0.5">{p.icon}</span>
                <span className="block leading-tight">{p.label}</span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 text-[11px]">
            <span className="font-mono font-bold text-cyan">
              {score.toFixed(1)}/10
            </span>
            <span className="text-muted-foreground">•</span>
            <span className="uppercase font-semibold text-foreground tracking-wide">
              {pattern}
            </span>
            <span className="text-muted-foreground">•</span>
            <span className="text-muted-foreground capitalize">{band}</span>
          </div>

          {/* Script Options — compact */}
          <div className="space-y-1.5">
            {scripts.map((s, i) => (
              <div
                key={s.key}
                className={cn(
                  "px-3 py-2 rounded-lg border transition-all",
                  s.color,
                  i === 0 && "ring-1 ring-foreground/10 shadow-sm"
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={cn("text-xs text-foreground", i === 0 ? "font-bold" : "font-semibold")}>
                        {s.label}
                      </p>
                      <span className="text-[10px] text-muted-foreground">
                        {s.role}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5 italic line-clamp-1">
                      {s.hook}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyScript(s.key)}
                    className="shrink-0 border-border text-foreground hover:bg-secondary h-7 text-[11px] px-2.5"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* The Rehearsal — 4th script type */}
          <div className={cn(
            "px-3 py-2 rounded-lg border transition-all",
            rehearsalData
              ? "text-amber-400 border-amber-500/30 bg-amber-500/5"
              : "text-muted-foreground border-border bg-secondary/20 opacity-60"
          )}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold text-foreground">🪨 The Rehearsal</p>
                  <span className="text-[10px] text-muted-foreground">Simulation-based preparation script</span>
                </div>
                {!rehearsalData && (
                  <p className="text-[10px] text-muted-foreground mt-1">Complete a rehearsal to unlock this script type</p>
                )}
              </div>
            </div>

            {rehearsalData && rehearsalLoading && (
              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" /> Generating rehearsal scripts...
              </div>
            )}

            {rehearsalData && rehearsalError && (
              <p className="mt-2 text-xs text-red-400">Could not generate rehearsal scripts. Try again.</p>
            )}

            {rehearsalData && rehearsalScripts && (
              <div className="mt-2 space-y-1.5">
                {[
                  { key: "the_rehearsal", label: "The Rehearsal", sublabel: "Full preparation narrative" },
                  { key: "simulation_script", label: "The Simulation", sublabel: "Punchy first-person account" },
                  { key: "the_breakdown", label: "The Breakdown", sublabel: "Educational tactic analysis" },
                ].map((s) => (
                  <div key={s.key} className="flex items-start gap-2 p-2.5 rounded-lg border border-border bg-card">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground mb-0.5">
                        {s.label} · {s.sublabel}
                      </p>
                      <p className="text-xs text-foreground leading-relaxed line-clamp-3">
                        {rehearsalScripts[s.key]}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        await navigator.clipboard.writeText(rehearsalScripts[s.key]);
                        toast.success("Script copied to clipboard");
                      }}
                      className="shrink-0 text-muted-foreground hover:text-cyan h-8 w-8 p-0 rounded-md transition-colors"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Variant Blocks */}
          <div className="space-y-1.5 pt-1.5 border-t border-border">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Content Variants
            </p>
            <VariantBlock title="Hook Variants" variants={hookVariants} isOpen={openVariant === "hooks"} onToggle={() => setOpenVariant(openVariant === "hooks" ? null : "hooks")} />
            <VariantBlock title="Caption Variants" variants={captionVariants} isOpen={openVariant === "captions"} onToggle={() => setOpenVariant(openVariant === "captions" ? null : "captions")} />
            <VariantBlock title="CTA / Title Variants" variants={ctaVariants} isOpen={openVariant === "ctas"} onToggle={() => setOpenVariant(openVariant === "ctas" ? null : "ctas")} />
          </div>

          {/* Rehearsal Variant Blocks — platform-filtered */}
          {rehearsalData && rehearsalScripts && (
            <div className="space-y-1.5 pt-1.5 border-t border-border">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Rehearsal Variants
              </p>
              {(() => {
                const pk = platform === "youtube" ? "yt" : platform === "carousel" ? "ig" : "tiktok";
                const hooks = (rehearsalScripts[`${pk}_hooks`] || []).map((t: string, i: number) => ({
                  style: `rhook-${i}`, label: `Hook ${i + 1}`, text: t,
                }));
                const captions = (rehearsalScripts[`${pk}_captions`] || []).map((t: string, i: number) => ({
                  style: `rcap-${i}`, label: `Caption ${i + 1}`, text: t,
                }));
                const ctas = (rehearsalScripts[`${pk}_ctas`] || []).map((t: string, i: number) => ({
                  style: `rcta-${i}`, label: `CTA ${i + 1}`, text: t,
                }));
                return (
                  <>
                    <VariantBlock title="Rehearsal Hooks" variants={hooks} isOpen={openVariant === "r-hooks"} onToggle={() => setOpenVariant(openVariant === "r-hooks" ? null : "r-hooks")} />
                    <VariantBlock title="Rehearsal Captions" variants={captions} isOpen={openVariant === "r-captions"} onToggle={() => setOpenVariant(openVariant === "r-captions" ? null : "r-captions")} />
                    <VariantBlock title="Rehearsal CTAs" variants={ctas} isOpen={openVariant === "r-ctas"} onToggle={() => setOpenVariant(openVariant === "r-ctas" ? null : "r-ctas")} />
                  </>
                );
              })()}
            </div>
          )}


          <div className="pt-1.5 border-t border-border space-y-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              {CARD_OPTIONS.map((opt) => {
                const isRehearsalDisabled = opt.key === "rehearsal" && !hasRehearsalData;
                const btn = (
                  <button
                    key={opt.key}
                    onClick={() => !isRehearsalDisabled && setCardType(opt.key)}
                    disabled={isRehearsalDisabled}
                    className={cn(
                      "flex-1 min-w-[calc(25%-6px)] px-2 py-1.5 rounded-md border text-center transition-all text-[10px]",
                      isRehearsalDisabled && "opacity-50 cursor-not-allowed",
                      cardType === opt.key
                        ? "border-cyan/40 bg-cyan/10 text-cyan font-semibold"
                        : "border-border bg-secondary/30 text-muted-foreground hover:bg-secondary/50"
                    )}
                  >
                    <span className="block font-medium text-[11px]">{opt.label}</span>
                    <span className="block opacity-60">{opt.desc}</span>
                  </button>
                );
                if (isRehearsalDisabled) {
                  return (
                    <TooltipProvider key={opt.key}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex-1 min-w-[calc(25%-6px)]">{btn}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Complete a rehearsal first</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                }
                return btn;
              })}
            </div>

            <Button
              onClick={downloadShareCard}
              disabled={isDownloading || (cardType === "rehearsal" && !hasRehearsalData)}
              variant="outline"
              className="w-full border-cyan/30 text-cyan hover:bg-cyan/10 h-8 text-xs"
            >
              {isDownloading ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Image className="w-3.5 h-3.5 mr-1.5" />
              )}
              Download {selectedCardOption.label}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hidden share cards for rendering */}
      <PatternFirstCard
        ref={patternCardRef}
        message={item.raw_text.slice(0, 200)}
        pattern={pattern}
        interpretation={interpretation}
        threatScore={analysis.manipulation_score ?? score}
        emotionalHarmScore={analysis.toxicity_score ?? Math.max(1, score - 1.5)}
      />
      <ViralPosterCard
        ref={creatorCardRef}
        message={item.raw_text.slice(0, 200)}
        score={score}
        pattern={pattern}
        interpretation={interpretation}
      />
      <EducationalExplainerCard
        ref={explainerCardRef}
        message={item.raw_text.slice(0, 200)}
        pattern={pattern}
        signals={[]}
        interpretation={interpretation}
        manipulationScore={analysis.manipulation_score ?? score}
        emotionalHarmScore={analysis.toxicity_score ?? Math.max(1, score - 1.5)}
      />
      {hasRehearsalData && (
        <RehearsalCard
          ref={rehearsalCardRef}
          tactics={rehearsalData!.tactics}
          readinessScore={rehearsalData!.readiness_score}
          exchangeCount={rehearsalData!.exchange_count}
        />
      )}
    </>
  );
}
